import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTimes, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { getSubscriberProfile, getCities, getHospitalsByCity, createHospitalBooking, getFamilyMembers, addFamilyMember, updateFamilyMember } from '../services/subscriberWalletService';
import PatientSelector from '../components/subscriber/PatientSelector';
import { formatRelationshipForApi, formatGenderForApi, calculateAgeFromDob } from '../utils/subscriberHelpers';

const extractApiError = (err, fallback) => {
  try {
    const match = err?.message?.match(/\{[\s\S]*\}/);
    if (match) {
      const parsed = JSON.parse(match[0]);
      const details = parsed?.msg?.details;
      if (details) {
        const msgs = Object.values(details).flat().filter(Boolean);
        if (msgs.length) return msgs[0];
      }
      return parsed?.msg?.error || parsed?.msg?.message || fallback;
    }
  } catch { /* ignore parse errors */ }
  return err?.message || fallback;
};

const ManualHospitalPaymentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoadingDataRef = useRef(false);

  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = { 'wallet_superadmin': 'SuperAdmin', 'wallet_ops': 'Ops' };
    return roleMapping[authRole] || null;
  })();

  const canAccess = currentRole === 'Ops' || currentRole === 'SuperAdmin';

  // Form state
  const [formData, setFormData] = useState({
    selectedCity: '',
    hospitalId: '',
    amount: '',
    reason: '',
    treatmentDetails: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // City dropdown
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityError, setCityError] = useState(null);
  const [citySearchText, setCitySearchText] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityInputRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const citySearchTimeoutRef = useRef(null);

  // Hospital dropdown
  const [hospitals, setHospitals] = useState([]);
  const [filteredHospitals, setFilteredHospitals] = useState([]);
  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const [hospitalError, setHospitalError] = useState(null);
  const [hospitalSearchText, setHospitalSearchText] = useState('');
  const [showHospitalDropdown, setShowHospitalDropdown] = useState(false);
  const hospitalInputRef = useRef(null);
  const hospitalDropdownRef = useRef(null);

  // Members
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [savingMember, setSavingMember] = useState(false);

  // Notification
  const [notification, setNotification] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 6000);
  };

  const fetchMembers = useCallback(async () => {
    if (!id || !currentRole) return;
    setLoadingMembers(true);
    try {
      const response = await getFamilyMembers(id, currentRole);
      let list = [];
      if (response?.msg?.items && Array.isArray(response.msg.items)) list = response.msg.items;
      else if (Array.isArray(response?.msg)) list = response.msg;
      else if (Array.isArray(response)) list = response;
      setMembers(
        list
          .filter(m => m.status !== 'Inactive')
          .map(m => ({
            id: m.member_id || m.id,
            name: m.name,
            relationship: m.relationship || m.relation,
            mobile: m.contact_number || m.contact || m.mobile || m.phone || '',
            gender: m.gender || '',
            age: m.age ?? calculateAgeFromDob(m.dob || m.date_of_birth),
            dob: m.dob || m.date_of_birth || '',
            email: m.email || '',
          }))
      );
    } catch {
      // non-blocking — user can still submit without member selection if API fails
    } finally {
      setLoadingMembers(false);
    }
  }, [id, currentRole]);

  const handleAddMember = async (memberData) => {
    setAddingMember(true);
    try {
      const apiData = {
        name: memberData.name.trim(),
        relation: formatRelationshipForApi(memberData.relationship),
        dob: memberData.dob,
        gender: formatGenderForApi(memberData.gender),
      };
      const age = calculateAgeFromDob(memberData.dob);
      if (age !== null) apiData.age = age;
      if (memberData.mobile?.replace(/\D/g, '').length === 10) apiData.contact_number = memberData.mobile.replace(/\D/g, '');
      if (memberData.email?.trim()) apiData.email = memberData.email.trim();

      const response = await addFamilyMember(id, currentRole, apiData);
      if (response?.metadata?.api_status === true) {
        await fetchMembers();
        showNotification(`${memberData.name} added successfully!`, 'success');
        return true;
      } else {
        showNotification(response?.msg?.error || response?.msg?.message || 'Failed to add member', 'error');
        return false;
      }
    } catch (err) {
      showNotification(extractApiError(err, 'Failed to add member'), 'error');
      return false;
    } finally {
      setAddingMember(false);
    }
  };

  const handleEditMember = async (memberId, memberData) => {
    setSavingMember(true);
    try {
      const apiData = {
        name: memberData.name.trim(),
        relation: formatRelationshipForApi(memberData.relationship),
        dob: memberData.dob,
        gender: formatGenderForApi(memberData.gender),
      };
      const age = calculateAgeFromDob(memberData.dob);
      if (age !== null) apiData.age = age;
      if (memberData.mobile?.replace(/\D/g, '').length === 10) apiData.contact_number = memberData.mobile.replace(/\D/g, '');
      if (memberData.email?.trim()) apiData.email = memberData.email.trim();

      const response = await updateFamilyMember(id, memberId, currentRole, apiData);
      if (response?.metadata?.api_status === true) {
        await fetchMembers();
        showNotification(`${memberData.name} updated successfully!`, 'success');
      } else {
        showNotification(response?.msg?.error || response?.msg?.message || 'Failed to update member', 'error');
      }
    } catch (err) {
      showNotification(extractApiError(err, 'Failed to update member'), 'error');
    } finally {
      setSavingMember(false);
    }
  };

  // Fetch cities
  const fetchCities = async (searchText = '') => {
    setLoadingCities(true);
    setCityError(null);
    try {
      const response = await getCities(searchText, currentRole);
      let citiesList = [];
      if (response?.metadata?.api_status === true) {
        citiesList = response.msg?.items || response.msg || [];
      } else if (Array.isArray(response)) {
        citiesList = response;
      }
      const cityNames = citiesList
        .filter(c => c && typeof c === 'object' && c.city)
        .map(c => c.city)
        .filter((c, i, self) => self.indexOf(c) === i)
        .sort();
      setCities(cityNames);
      setFilteredCities(cityNames);
    } catch {
      setCityError('Failed to load cities.');
    } finally {
      setLoadingCities(false);
    }
  };

  // Initial city load + debounced search
  useEffect(() => {
    if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
    citySearchTimeoutRef.current = setTimeout(() => { fetchCities(citySearchText); }, 300);
    return () => { if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySearchText]);

  useEffect(() => {
    if (!citySearchText.trim()) setFilteredCities(cities);
    else setFilteredCities(cities.filter(c => c.toLowerCase().includes(citySearchText.toLowerCase())));
  }, [citySearchText, cities]);

  useEffect(() => {
    if (!hospitalSearchText.trim()) setFilteredHospitals(hospitals);
    else setFilteredHospitals(hospitals.filter(h => h.name.toLowerCase().includes(hospitalSearchText.toLowerCase()) || (h.address && h.address.toLowerCase().includes(hospitalSearchText.toLowerCase()))));
  }, [hospitalSearchText, hospitals]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cityInputRef.current && cityDropdownRef.current && !cityInputRef.current.contains(e.target) && !cityDropdownRef.current.contains(e.target)) {
        setShowCityDropdown(false);
      }
      if (hospitalInputRef.current && hospitalDropdownRef.current && !hospitalInputRef.current.contains(e.target) && !hospitalDropdownRef.current.contains(e.target)) {
        setShowHospitalDropdown(false);
      }

    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch hospitals by city
  const fetchHospitals = async (city) => {
    if (!city) { setHospitals([]); setFilteredHospitals([]); return; }
    setLoadingHospitals(true);
    setHospitalError(null);
    setHospitals([]);
    setFilteredHospitals([]);
    try {
      const response = await getHospitalsByCity(city, '', '', currentRole);
      let list = [];
      if (response?.metadata?.api_status === true) {
        list = response.msg?.items || response.msg || [];
      } else if (Array.isArray(response)) {
        list = response;
      }
      const formatted = list.map(h => ({
        id: h.id || h.hospital_id,
        name: h.name || h.hospital_name || 'Unknown Hospital',
        address: h.address_line || '',
      })).filter(h => h.id);
      setHospitals(formatted);
      setFilteredHospitals(formatted);
      if (formatted.length === 1) {
        setFormData(prev => ({ ...prev, hospitalId: formatted[0].id }));
        setHospitalSearchText(formatted[0].name);
      }
    } catch {
      setHospitalError('Failed to load hospitals.');
    } finally {
      setLoadingHospitals(false);
    }
  };

  // Load subscriber profile
  useEffect(() => {
    const loadData = async () => {
      if (isLoadingDataRef.current) return;
      try {
        setLoading(true);
        setError(null);
        if (!id || !currentRole) { setError('Invalid request'); setLoading(false); return; }
        isLoadingDataRef.current = true;
        const profileData = await getSubscriberProfile(id, currentRole);
        let profileToSet = null;
        const src = profileData?.msg || profileData?.data || profileData;
        if (src && typeof src === 'object') {
          profileToSet = {
            id: src.id || id,
            personal: src.personal || {},
            plan: src.plan || src.personal_subscription || src.subscription_plan || '',
            status: src.status || '',
          };
        }
        if (profileToSet) setProfile(profileToSet);
      } catch {
        setError('Failed to load subscriber profile');
      } finally {
        setLoading(false);
        isLoadingDataRef.current = false;
      }
    };
    loadData();
  }, [id, currentRole]);

  useEffect(() => {
    if (profile) fetchMembers();
  }, [profile, fetchMembers]);

  const validateForm = () => {
    const errors = {};
    if (!selectedMemberId) errors.selectedMemberId = 'Patient selection is required';
    if (!formData.selectedCity) errors.selectedCity = 'City is required';
    if (!formData.hospitalId) errors.hospitalId = 'Hospital is required';
    if (!formData.amount || parseFloat(formData.amount) <= 0) errors.amount = 'Valid bill amount is required';
    if (!formData.reason.trim()) errors.reason = 'Treatment reason is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        application_id: id,
        member_id: selectedMemberId,
        hospital_id: formData.hospitalId,
        amount: parseFloat(formData.amount),
        reason: formData.reason.trim(),
        treatment_details: null,
      };
      await createHospitalBooking(currentRole, payload);
      showNotification(
        'Payment link created successfully',
        'success'
      );
      setFormData({ selectedCity: '', hospitalId: '', amount: '', reason: '', treatmentDetails: null });
      setSelectedMemberId('');
      setCitySearchText('');
      setHospitalSearchText('');
      setHospitals([]);
      setFilteredHospitals([]);
    } catch (err) {
      let msg = 'Failed to create booking. Please try again.';
      if (err?.message) {
        try {
          const match = err.message.match(/\{[\s\S]*\}/);
          if (match) {
            const parsed = JSON.parse(match[0]);
            msg = parsed?.msg?.error || parsed?.error || msg;
          } else {
            msg = err.message;
          }
        } catch {
          msg = err.message;
        }
      }
      showNotification(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentRole || !canAccess) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">This feature is only available for Ops and SuperAdmin roles.</p>
          <button onClick={() => navigate('/subscriber-wallet')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Go to Home</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Profile not found'}</p>
          <button onClick={() => navigate('/subscriber-wallet/hospital-payment')} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Back to Search</button>
        </div>
      </div>
    );
  }

  const isFormIncomplete = !selectedMemberId || !formData.selectedCity || !formData.hospitalId || !formData.amount || parseFloat(formData.amount) <= 0 || !formData.reason.trim();

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate('/subscriber-wallet')} className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600">
            ← Back to Home
          </button>
          <button onClick={() => navigate(`/subscriber-wallet/subscriber/${id}`)} className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600">
            ← Back to Subscriber
          </button>
        </div>

        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manual Hospital Payment</h1>
          <p className="text-gray-500 mt-1">Create a hospital payment link for the subscriber</p>
        </div>

        {/* Subscriber Info */}
        <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm">
          <div className="text-xl font-bold text-gray-900">{profile?.personal?.full_name || 'Subscriber'}</div>
          <div className="text-sm text-gray-600 mt-1">Mobile: {profile?.personal?.mobile || 'N/A'}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">Plan</div>
              <div className="text-sm font-medium text-gray-900">{profile?.plan || 'N/A'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Status</div>
              <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-md w-fit ${profile?.status === 'Active' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'}`}>
                {profile?.status || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Booking Form */}
        <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Booking Details</h2>
            <p className="text-sm text-gray-500 mt-1">Fill in the hospital and treatment details to generate a payment link</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Patient Selection */}
            <PatientSelector
              members={members}
              loading={loadingMembers}
              value={selectedMemberId}
              onChange={(id) => {
                setSelectedMemberId(id);
                if (formErrors.selectedMemberId) setFormErrors(prev => ({ ...prev, selectedMemberId: '' }));
              }}
              onAddMember={handleAddMember}
              onEditMember={handleEditMember}
              addingMember={addingMember}
              savingMember={savingMember}
              error={formErrors.selectedMemberId}
              mounted={mounted}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City <span className="text-red-500">*</span></label>
                <div className="relative" ref={cityInputRef}>
                  <input
                    type="text"
                    value={formData.selectedCity || citySearchText}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCitySearchText(val);
                      setShowCityDropdown(true);
                      if (formData.selectedCity && val !== formData.selectedCity) {
                        setFormData(prev => ({ ...prev, selectedCity: '', hospitalId: '' }));
                        setHospitals([]);
                        setFilteredHospitals([]);
                        setHospitalSearchText('');
                      }
                    }}
                    onFocus={() => setShowCityDropdown(true)}
                    placeholder={loadingCities ? 'Loading cities...' : 'Search and select city...'}
                    disabled={loadingCities}
                    className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${formErrors.selectedCity ? 'border-red-500' : 'border-gray-300'} ${loadingCities ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {showCityDropdown && filteredCities.length > 0 && (
                    <div ref={cityDropdownRef} className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredCities.map((city, i) => (
                        <div
                          key={`${city}-${i}`}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, selectedCity: city, hospitalId: '' }));
                            setCitySearchText(city);
                            setShowCityDropdown(false);
                            setHospitalSearchText('');
                            fetchHospitals(city);
                          }}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          {city}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {cityError && <p className="mt-1 text-sm text-red-600">{cityError}</p>}
                {formErrors.selectedCity && <p className="mt-1 text-sm text-red-600">{formErrors.selectedCity}</p>}
              </div>

              {/* Hospital */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hospital <span className="text-red-500">*</span></label>
                <div className="relative" ref={hospitalInputRef}>
                  <input
                    type="text"
                    value={(() => { const h = hospitals.find(h => h.id === formData.hospitalId); return h ? h.name : hospitalSearchText; })()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setHospitalSearchText(val);
                      setShowHospitalDropdown(true);
                      if (formData.hospitalId) {
                        const h = hospitals.find(h => h.id === formData.hospitalId);
                        if (h && val !== h.name) setFormData(prev => ({ ...prev, hospitalId: '' }));
                      }
                    }}
                    onFocus={() => { if (hospitals.length > 0) setShowHospitalDropdown(true); }}
                    placeholder={
                      !formData.selectedCity ? 'Select city first' :
                      loadingHospitals ? 'Loading hospitals...' :
                      hospitals.length === 0 && !hospitalError ? 'No hospitals found' :
                      'Search and select hospital...'
                    }
                    disabled={!formData.selectedCity || loadingHospitals}
                    className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${formErrors.hospitalId ? 'border-red-500' : 'border-gray-300'} ${(!formData.selectedCity || loadingHospitals) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {showHospitalDropdown && filteredHospitals.length > 0 && (
                    <div ref={hospitalDropdownRef} className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredHospitals.map((h) => (
                        <div
                          key={h.id}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, hospitalId: h.id }));
                            setHospitalSearchText(h.name);
                            setShowHospitalDropdown(false);
                          }}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium">{h.name}</div>
                          {h.address && <div className="text-sm text-gray-500 mt-0.5">{h.address}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {hospitalError && <p className="mt-1 text-sm text-red-600">{hospitalError}</p>}
                {formErrors.hospitalId && <p className="mt-1 text-sm text-red-600">{formErrors.hospitalId}</p>}
              </div>

              {/* Bill Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bill Amount (₹) <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => { setFormData(prev => ({ ...prev, amount: e.target.value })); if (formErrors.amount) setFormErrors(prev => ({ ...prev, amount: '' })); }}
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${formErrors.amount ? 'border-red-500' : 'border-gray-300'}`}
                />
                {formErrors.amount && <p className="mt-1 text-sm text-red-600">{formErrors.amount}</p>}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Treatment Reason <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={(e) => { setFormData(prev => ({ ...prev, reason: e.target.value })); if (formErrors.reason) setFormErrors(prev => ({ ...prev, reason: '' })); }}
                  maxLength={500}
                  placeholder="e.g. Knee surgery, Fracture treatment..."
                  className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${formErrors.reason ? 'border-red-500' : 'border-gray-300'}`}
                />
                {formErrors.reason && <p className="mt-1 text-sm text-red-600">{formErrors.reason}</p>}
              </div>

            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                type="submit"
                disabled={isSubmitting || isFormIncomplete}
                className={`px-6 py-2.5 rounded-md font-medium text-white transition-colors ${(isSubmitting || isFormIncomplete) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'}`}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </span>
                ) : 'Send Payment Link'}
              </button>
            </div>
          </form>
        </div>


        {mounted && notification && createPortal(
          <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center z-[10000] pointer-events-none">
            <div className="pointer-events-auto transform transition-all duration-300 ease-out">
              <div className={`flex items-center space-x-3 px-6 py-4 rounded-lg shadow-2xl border-2 min-w-[320px] max-w-lg ${notification.type === 'success' ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 text-green-800' : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300 text-red-800'}`}>
                <div className={`flex-shrink-0 ${notification.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {notification.type === 'success' ? <FaCheckCircle className="w-6 h-6" /> : <FaExclamationCircle className="w-6 h-6" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold leading-relaxed">{notification.message}</p>
                </div>
                <button onClick={() => setNotification(null)} className={`flex-shrink-0 ml-2 p-1 rounded-full hover:bg-opacity-20 transition-colors ${notification.type === 'success' ? 'text-green-600 hover:bg-green-600' : 'text-red-600 hover:bg-red-600'}`}>
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default ManualHospitalPaymentForm;
